import { ApolloError } from '@apollo/client';
import { useFormik } from "formik";
import { useRouter } from "next/router";
import * as yup from 'yup';
import { useToast } from "@netfront/ekardo-content-library";
import { Button, Input, PasswordInput } from "@netfront/ui-library";
import { IResetPasswordFormFields } from "./ResetPasswordForm.interfaces";
import { IResetPasswordOnCompletedResponse, useResetPassword } from "@netfront/gelada-identity-library";

export const ResetPasswordForm = () => {
    const { push, query: { code } } = useRouter();
    const { handleToastError, handleToastSuccess } = useToast();
    
    const handleResetPasswordCompleted = (data: IResetPasswordOnCompletedResponse) => {
      if(!data) return;

      handleToastSuccess({
        message: 'Password reset successfully'
      })

      push('/login').catch((error) =>
        handleToastError({
          shouldUseFriendlyErrorMessage: true,
          error,
        }),
      );
    };

    const { handleResetPassword, isLoading: isSubmitting = false } = useResetPassword({
      onCompleted: handleResetPasswordCompleted,
      onError: (error) => {
        handleToastError({
          error,
          shouldUseFriendlyErrorMessage: true,
        });
      },
    });
    
    const formik = useFormik<IResetPasswordFormFields>({
      initialValues: {
        password: '',
        confirmPassword: '',
      },
      validationSchema: yup.object().shape({
        password: yup.string().required('Please enter in a password'),
        confirmPassword: yup.string()
          .required('Please confirm your password')
          .oneOf([yup.ref('password')], 'Passwords do not match'),
      }),
      onSubmit: async ({ password }: IResetPasswordFormFields) => {
        handleResetPassword({
          code: String(code),
          newPassword: password,
        });
      }
    });

    const { errors, touched, handleBlur, handleChange, handleSubmit, isValid, dirty } = formik;
  
    return (
      <form onSubmit={handleSubmit}>
        <PasswordInput
          errorMessage={touched.password && errors.password ? errors.password : ''}
          id='password'
          labelText='Password'
          name='password'
          placeholder='Enter password'
          isRequired
          onChange={handleChange}
          onBlur={handleBlur}
        />

        <PasswordInput
          errorMessage={touched.confirmPassword && errors.confirmPassword ? errors.confirmPassword : ''}
          id='confirmPassword'
          labelText='Confirm password'
          name='confirmPassword'
          placeholder='Re-enter password'
          isRequired
          onChange={handleChange}
          onBlur={handleBlur}
        />

        <Button
          additionalClassNames='w-full'
          variant='tertiary'
          text='Reset password'
          type='submit'
          isDisabled={isSubmitting || !isValid || !dirty}
          isLoading={isSubmitting}
        />
      </form>
    )
};